/**
 * @fileoverview App Sidebar
 *
 * Purpose: Main sidebar navigation for the dashboard using shadcn Sidebar.
 * Styled to match the app's existing card/primary green colour scheme.
 * Contains org switcher, navigation links (with job sub-navigation), and user menu.
 *
 * Dependencies:
 * - shadcn Sidebar components (src/ui/layout/sidebar.tsx)
 * - Active org context (src/utils/org-context.tsx)
 * - Lucide icons
 *
 * Used by:
 * - Dashboard layout (_layout.tsx)
 */

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
} from "@/ui/layout/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { UserAvatar, getDisplayName } from "@/ui/composed";
import { Logo } from "@/ui/layout/logo";
import { useActiveOrg } from "@/utils/org-context";
import { cn, useSignOut, modKeyLabel } from "@/utils/misc";
import {
  Link,
  useMatchRoute,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import {
  Fragment,
  useEffect,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import {
  Briefcase,
  Settings,
  ChevronsUpDown,
  ChevronUp,
  LogOut,
  Building2,
  Check,
  Shield,
  ShieldCheck,
  ClipboardList,
  Files,
  Database,
  Globe,
  ScanSearch,
  FileText,
  Scale,
  User,
  CreditCard,
  Hash,
  Palette,
  FileStack,
  TextCursorInput,
  Library,
  ScrollText,
  Stamp,
  LifeBuoy,
  Search,
  Users,
  Contact,
  CalendarDays,
  PlugZap,
  Zap,
  Bell,
  Inbox,
  Mail,
  ChevronRight,
  ListChecks,
  TableProperties,
  CircleDot,
  Tag,
  SquareKanban,
  Workflow,
  LayoutTemplate,
  UserCog,
  Timer,
  BadgeCheck,
  ChartColumn,
} from "lucide-react";
import { ThemeSwitcherSidebar } from "@/ui/settings";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { User as UserType } from "~/types";
import {
  dataAgentsForJob,
  dataAgentLabel,
} from "@/components/jobs/phases/gather/summarization/agentRegistry";
import { caseTypeFromAppointmentType } from "@/lib/precedent-templates";
import { reviewAgentsForJob } from "@/components/jobs/phases/review/reviewRegistry";
import {
  POD_INTAKE_AGENT_KEY,
  RECOVERY_SECTIONS,
  RECOVERY_SECTIONS_BY_SLUG,
} from "@/components/jobs/phases/recoveries/recoveriesRegistry";
import { NEW_OUTPUT_ACCENT_CLASS } from "@/components/jobs/phases/_shared/use-new-output-boundary";
import { useCommandPalette } from "@/components/agents/universal/CommandPaletteContext";
import { useLocalToday } from "@/ui/hooks/use-local-today";

/**
 * Sidebar-sized Morgan mark. The shared `Logo` defaults to the full
 * wordmark; this thin wrapper locks `compact` on so we get the same
 * icon-only bars used in the sidebar header, and forwards `className`
 * so phase rendering (`<phase.icon className="size-3" />`) keeps
 * working unchanged.
 */
function MorganMark({ className }: { className?: string }) {
  return <Logo compact className={className} />;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

/**
 * Main application sidebar with org switcher, navigation, and user menu.
 * Styled to match the app's card-based UI with primary green accents.
 *
 * @param user - The current authenticated user
 */
export function AppSidebar({ user }: { user: UserType }) {
  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader className="h-16 justify-center border-b border-sidebar-border px-4 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0">
        <LogoHeader />
      </SidebarHeader>
      <OrgSwitcher />
      <SidebarContent>
        <NavMain user={user} />
      </SidebarContent>
      <SidebarSeparator />
      <SidebarFooter className="p-2">
        <UserMenu user={user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

// ---------------------------------------------------------------------------
// Logo Header
// ---------------------------------------------------------------------------

/**
 * Logo displayed at the top of the sidebar.
 * Shows compact (icon) or full logo based on sidebar state.
 */
function LogoHeader() {
  return (
    <Link to="/dashboard/jobs" className="flex items-center gap-2">
      <Logo
        compact
        className="h-8 w-8 shrink-0 text-primary group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-6"
      />
      <span className="truncate font-serif text-lg font-medium text-primary group-data-[collapsible=icon]:hidden">
        Morgan
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Org Switcher
// ---------------------------------------------------------------------------

/**
 * Dropdown in the sidebar for switching between organisations.
 */
function OrgSwitcher() {
  const { activeOrg, userOrgs, setOrgId } = useActiveOrg();

  return (
    <div className="flex h-16 shrink-0 items-center border-b border-sidebar-border px-2">
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton size="lg">
                <div className="flex aspect-square size-8 items-center justify-center rounded-md border border-border bg-secondary">
                  <Building2 className="size-4 text-primary/60" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium text-primary/80">
                    {activeOrg?.name ?? "Select Organisation"}
                  </span>
                  <span className="truncate text-xs text-primary/50">
                    Organisation
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4 text-primary/40" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg bg-card"
              side="bottom"
              align="start"
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs text-primary/60">
                Organisations
              </DropdownMenuLabel>
              {userOrgs.map((org) => (
                <DropdownMenuItem
                  key={org._id}
                  onClick={() => setOrgId(org._id)}
                  className="gap-2 p-2"
                >
                  <div className="flex size-6 items-center justify-center rounded-sm border border-border">
                    <Building2 className="size-3.5 text-primary/60" />
                  </div>
                  <span className="flex-1 truncate text-primary/80">
                    {org.name}
                  </span>
                  {activeOrg?._id === org._id && (
                    <Check className="ml-auto size-4 text-primary/60" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

/** The three collapsible accordion groups in the per-job nav. */
type NavGroupKey = "gather" | "data" | "investigations";

/** One row in the per-job phase navigation. */
type JobPhaseItem = {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
};

/**
 * Per-job phase items. The `agent` slot uses the Morgan logo bars
 * (compact) rather than a generic Sparkles icon to reinforce the
 * Morgan-AI branding on the in-job agent surface.
 *
 * Data and Investigations render as collapsible `NavGroup` accordions; the
 * "Gather Information" group (Searches → Day One → Files, see `GATHER_ITEMS`)
 * is another `NavGroup` rendered after Mail (which sits under Job Chat).
 */
const JOB_PHASE_ITEMS: ReadonlyArray<JobPhaseItem> = [
  { key: "plan", label: "Overview", icon: ClipboardList },
  { key: "agent", label: "Job Chat", icon: MorganMark },
  { key: "mail", label: "Inbox", icon: Mail },
  { key: "summaries", label: "Data", icon: Database },
  { key: "review", label: "Investigations", icon: ScanSearch },
  { key: "reporting", label: "Reporting", icon: FileText },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "contacts", label: "Contacts", icon: Users },
  { key: "timesheets", label: "Timesheets", icon: Timer },
  { key: "settings", label: "Job Settings", icon: Settings },
];

/**
 * Phase entries that only make sense once the instrument of appointment has
 * landed. Pre-appointment jobs (Flow A — pitching/pre-engagement) hide these;
 * they reappear automatically once the job is converted via
 * `convertPreAppointmentJob` and its status leaves `pre_appointment`.
 *
 * Data (`summaries`) is kept visible on pre-appointment jobs so practitioners
 * can watch the Corporate agent finish; its other agents are filtered out
 * via `dataAgentsForJob` in the registry.
 */
const POST_APPOINTMENT_ONLY_PHASES: ReadonlySet<string> = new Set([
  "review",
  "reporting",
]);

/**
 * Children of the collapsible "Gather Information" section. Searches keeps its
 * own nested sub-tabs (Registry / Web / KYC) when active.
 */
const GATHER_ITEMS: ReadonlyArray<JobPhaseItem> = [
  { key: "searches", label: "Searches", icon: Search },
  { key: "day-one", label: "Document Generator", icon: Stamp },
  { key: "gather", label: "Files", icon: Files },
];

const SETTINGS_ITEMS = [
  { key: "", label: "General", icon: User },
  { key: "organization", label: "Organisation", icon: Building2 },
  { key: "jobcode", label: "Job Codes", icon: Hash },
  { key: "contact-roles", label: "Contact Roles", icon: Tag },
  { key: "public-holidays", label: "Public Holidays", icon: CalendarDays },
  { key: "billing", label: "Billing", icon: CreditCard },
  { key: "integrations", label: "Integrations", icon: PlugZap },
] as const;

const PRECEDENT_ITEMS = [
  { key: "branding", label: "Branding", icon: Palette },
  { key: "docs", label: "Docs", icon: FileStack },
  { key: "snippets", label: "Variants", icon: Library },
  { key: "fields", label: "Fields", icon: TextCursorInput },
  { key: "report-templates", label: "Report Templates", icon: ScrollText },
] as const;

const FIRM_CONTACT_ITEMS = [
  { key: "", label: "Directory", icon: Contact },
  { key: "categories", label: "Categories", icon: Tag },
  { key: "fields", label: "Fields", icon: TextCursorInput },
] as const;

// ---------------------------------------------------------------------------
// Agent activity badges (MOR-1013)
// ---------------------------------------------------------------------------

type JobAgentRow = {
  agentKey: string;
  agentName: string;
  phase: "data" | "investigations" | "reporting" | "recoveries";
  runStatus: "running" | "completed" | "failed";
  updatedAt: number;
};

/** The current user's unseen agent updates on a job, one row per agent. */
function useJobAgentActivity(jobId: string | undefined): JobAgentRow[] {
  const { data } = useQuery({
    ...convexQueryOrIdle(
      api.notifications.agentActivity.forJob,
      jobId ? { jobId: jobId as Id<"jobs"> } : undefined,
    ),
    enabled: !!jobId,
  });
  return (data ?? []) as JobAgentRow[];
}

/** Marks a job's agent updates seen (in-progress rows stay). */
function useMarkAgentSeen(jobId: string) {
  const { mutate } = useMutation({
    mutationFn: useConvexMutation(api.notifications.agentActivity.markSeen),
  });
  return (scope: { agentKeys?: string[]; phase?: JobAgentRow["phase"] }) =>
    mutate({ jobId: jobId as Id<"jobs">, ...scope });
}

/**
 * Which rows belong to a sidebar item. The recoverable-transaction and
 * offence heads run as separate agents but sit under one Investigations
 * row each, so the master ids match their whole head family.
 */
function rowsForAgent(rows: JobAgentRow[], agentId: string): JobAgentRow[] {
  if (agentId === "sum_flagged_master") {
    return rows.filter((r) => r.agentKey.startsWith("sum_flagged"));
  }
  if (agentId === "sum_offence_master") {
    return rows.filter((r) => r.agentKey.startsWith("sum_offence"));
  }
  return rows.filter((r) => r.agentKey === agentId);
}

const PILL_BASE =
  "rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none text-white";
// Green hierarchy shared with the "New" output badge: completed uses the same
// primary green as the badge (a finished run = a new result to review), running
// is a lighter green (in progress), and failures stay red so errors stand out.
const PILL_COLOR = {
  running: "bg-emerald-400",
  completed: NEW_OUTPUT_ACCENT_CLASS,
  failed: "bg-destructive",
} as const;

/**
 * Per-status pills for an agent row: primary green = complete (a new result),
 * lighter green = in progress, red = failed. Renders nothing when there is
 * nothing unseen.
 */
function AgentStatusPills({ rows }: { rows: JobAgentRow[] }) {
  if (rows.length === 0) return null;
  const counts = { running: 0, completed: 0, failed: 0 };
  for (const r of rows) counts[r.runStatus] += 1;
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1">
      {(["running", "completed", "failed"] as const).map((status) =>
        counts[status] > 0 ? (
          <span key={status} className={cn(PILL_BASE, PILL_COLOR[status])}>
            {counts[status]}
          </span>
        ) : null,
      )}
    </span>
  );
}

/**
 * Single roll-up pill for a group header (total unseen), coloured by the
 * most urgent state present: in progress, else failed, else complete.
 */
function AgentGroupPill({ rows }: { rows: JobAgentRow[] }) {
  if (rows.length === 0) return null;
  const status = rows.some((r) => r.runStatus === "running")
    ? "running"
    : rows.some((r) => r.runStatus === "failed")
      ? "failed"
      : "completed";
  return (
    <span className={cn("ml-auto mr-5", PILL_BASE, PILL_COLOR[status])}>
      {rows.length >= 100 ? "99+" : rows.length}
    </span>
  );
}

/**
 * Sub-items shown under Investigations in the sidebar. Each entry (including
 * the Books & Records workspace, which is a customView entry) links to
 * /dashboard/jobs/$jobId/review/<slug>.
 */
function ReviewSubTabs({
  jobId,
  appointmentType,
  agentRows,
}: {
  jobId: string;
  appointmentType: string | undefined;
  agentRows: JobAgentRow[];
}) {
  const params = useParams({ strict: false }) as { agent?: string };
  const activeAgent = params.agent;
  const markSeen = useMarkAgentSeen(jobId);
  const agents = reviewAgentsForJob(appointmentType);

  return (
    <SidebarMenuSub>
      {agents.map((item) => {
        const rows = rowsForAgent(agentRows, item.dataAgentId);
        return (
          <SidebarMenuSubItem key={item.slug}>
            <SidebarMenuSubButton
              asChild
              size="sm"
              isActive={activeAgent === item.slug}
            >
              <Link
                to="/dashboard/jobs/$jobId/review/$agent"
                params={{ jobId, agent: item.slug }}
                onClick={() =>
                  rows.length > 0 &&
                  markSeen({ agentKeys: rows.map((r) => r.agentKey) })
                }
              >
                <item.icon className="size-3" />
                <span className="truncate">{item.label}</span>
                <AgentStatusPills rows={rows} />
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
    </SidebarMenuSub>
  );
}

/**
 * Proof of Debt as a top-level job-nav row (sibling of Investigations /
 * Reporting). The Recoveries phase is hidden for now; this is the
 * surviving entry, likely to become Creditor Management later.
 *
 * Unseen Form 535 intake (MOR-1013) lands on this row the same way Data
 * / Investigations sub-items get their pills — Recoveries itself is
 * hidden, so the group-level roll-up never rendered.
 */
function ProofOfDebtRow({
  jobId,
  agentRows,
}: {
  jobId: string;
  agentRows: JobAgentRow[];
}) {
  const matchRoute = useMatchRoute();
  const markSeen = useMarkAgentSeen(jobId);
  const entry = RECOVERY_SECTIONS_BY_SLUG.get("proof-of-debt");
  const active = !!matchRoute({
    to: "/dashboard/jobs/$jobId/recoveries/$section",
    params: { jobId, section: "proof-of-debt" },
    fuzzy: false,
  });
  if (!entry) return null;
  const Icon = entry.icon;
  const rows = rowsForAgent(agentRows, POD_INTAKE_AGENT_KEY);

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild size="sm" isActive={active}>
        <Link
          to="/dashboard/jobs/$jobId/recoveries/$section"
          params={{ jobId, section: entry.slug }}
          onClick={() =>
            rows.length > 0 &&
            markSeen({ agentKeys: rows.map((r) => r.agentKey) })
          }
        >
          <Icon className="size-3.5" />
          <span className="truncate">{entry.label}</span>
          <AgentStatusPills rows={rows} />
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

/**
 * Sub-items shown under Calendar in the sidebar. Two children today:
 * Key Dates (the calendar surface, default) and Review Windows (the
 * recovery-windows table sibling route). Mirrors the Data sub-tabs
 * pattern below so the visual rhythm matches the rest of the
 * phase nav.
 */
function CalendarSubTabs({ jobId }: { jobId: string }) {
  const matchRoute = useMatchRoute();

  // Match each child route explicitly so the active highlight reflects
  // the actual URL the user is on.
  const reviewActive = !!matchRoute({
    to: "/dashboard/jobs/$jobId/calendar/review-windows",
    params: { jobId },
    fuzzy: false,
  });
  const keyDatesActive = !!matchRoute({
    to: "/dashboard/jobs/$jobId/calendar",
    params: { jobId },
    fuzzy: false,
  });
  const tasksActive = !!matchRoute({
    to: "/dashboard/jobs/$jobId/calendar/tasks",
    params: { jobId },
    fuzzy: false,
  });

  return (
    <SidebarMenuSub>
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild size="sm" isActive={tasksActive}>
          <Link to="/dashboard/jobs/$jobId/calendar/tasks" params={{ jobId }}>
            <SquareKanban className="size-3" />
            <span>Tasks</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild size="sm" isActive={keyDatesActive}>
          <Link to="/dashboard/jobs/$jobId/calendar" params={{ jobId }}>
            <CalendarDays className="size-3" />
            <span>Calendar</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild size="sm" isActive={reviewActive}>
          <Link
            to="/dashboard/jobs/$jobId/calendar/review-windows"
            params={{ jobId }}
          >
            <Scale className="size-3" />
            <span>Review Windows</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    </SidebarMenuSub>
  );
}

/**
 * Sub-items under Searches: Registry searches (InfoTrack), the AI Web search
 * tool, and KYC & AML checks (also via InfoTrack). All are available to every
 * job member; the InfoTrack-backed tabs are in beta and require the firm's
 * own InfoTrack API key to run.
 */
function SearchesSubTabs({ jobId }: { jobId: string }) {
  const matchRoute = useMatchRoute();

  const infotrackActive = !!matchRoute({
    to: "/dashboard/jobs/$jobId/searches/infotrack",
    params: { jobId },
    fuzzy: false,
  });
  const webActive = !!matchRoute({
    to: "/dashboard/jobs/$jobId/searches/web",
    params: { jobId },
    fuzzy: false,
  });
  const kycActive = !!matchRoute({
    to: "/dashboard/jobs/$jobId/searches/kyc-aml",
    params: { jobId },
    fuzzy: false,
  });

  return (
    <SidebarMenuSub>
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild size="sm" isActive={infotrackActive}>
          <Link
            to="/dashboard/jobs/$jobId/searches/infotrack"
            params={{ jobId }}
          >
            <Database className="size-3" />
            <span>Registry searches</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild size="sm" isActive={webActive}>
          <Link to="/dashboard/jobs/$jobId/searches/web" params={{ jobId }}>
            <Globe className="size-3" />
            <span>Web search</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild size="sm" isActive={kycActive}>
          <Link to="/dashboard/jobs/$jobId/searches/kyc-aml" params={{ jobId }}>
            <ShieldCheck className="size-3" />
            <span>KYC &amp; AML</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    </SidebarMenuSub>
  );
}

/**
 * Sub-items shown under Data in the sidebar — one entry per summarization
 * agent. Slug + icon come from the shared registry so this list stays in
 * lock-step with the AgentCard rows on /summaries.
 *
 * Each links to /dashboard/jobs/$jobId/summaries/<slug>; the parent "Data"
 * item links to /summaries (the all-agents summary view). Pre-appointment
 * jobs only list agents flagged `visibleOnPreAppointment` (Corporate).
 */
function DataSubTabs({
  jobId,
  jobStatus,
  appointmentType,
  agentRows,
}: {
  jobId: string;
  jobStatus: string | undefined;
  appointmentType: string | undefined;
  agentRows: JobAgentRow[];
}) {
  const params = useParams({ strict: false }) as { agent?: string };
  const activeAgent = params.agent;
  const agents = dataAgentsForJob(jobStatus, appointmentType);
  const markSeen = useMarkAgentSeen(jobId);
  // Personal-insolvency jobs relabel the Corporate agent ("Structure"); every
  // corporate appointment keeps the default labels.
  const isBankruptcy =
    caseTypeFromAppointmentType(appointmentType) === "personal";

  return (
    <SidebarMenuSub>
      {agents.map((item) => {
        const rows = rowsForAgent(agentRows, item.id);
        return (
          <SidebarMenuSubItem key={item.slug}>
            <SidebarMenuSubButton
              asChild
              size="sm"
              isActive={activeAgent === item.slug}
            >
              <Link
                to="/dashboard/jobs/$jobId/summaries/$agent"
                params={{ jobId, agent: item.slug }}
                onClick={() =>
                  rows.length > 0 && markSeen({ agentKeys: [item.id] })
                }
              >
                <item.icon className="size-3" />
                <span className="truncate">
                  {dataAgentLabel(item, isBankruptcy)}
                </span>
                <AgentStatusPills rows={rows} />
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
    </SidebarMenuSub>
  );
}

/**
 * One per-job phase row plus its nested sub-tabs (when its route is active).
 * Shared by the flat phase list and the Gather Information group.
 */
function JobPhaseRow({ jobId, phase }: { jobId: string; phase: JobPhaseItem }) {
  const matchRoute = useMatchRoute();

  // Per-job Mail badge (MOR-529): unread inbound + drafts awaiting review,
  // mirroring the notifications-inbox badge pattern.
  const { data: mailBadge } = useQuery({
    ...convexQuery(api.mail.queries.getMailBadge, {
      jobId: jobId as Id<"jobs">,
    }),
    enabled: phase.key === "mail",
  });
  const mailBadgeCount =
    phase.key === "mail"
      ? (mailBadge?.unread ?? 0) + (mailBadge?.pendingDrafts ?? 0)
      : 0;
  // Phases with sub-routes need fuzzy matching so the parent stays highlighted
  // while a child route is open.
  const fuzzy =
    phase.key === "review" ||
    phase.key === "summaries" ||
    phase.key === "calendar" ||
    phase.key === "searches";
  const phaseActive = matchRoute({
    to: `/dashboard/jobs/$jobId/${phase.key}`,
    params: { jobId },
    fuzzy,
  });

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild size="sm" isActive={!!phaseActive}>
        <Link to={`/dashboard/jobs/$jobId/${phase.key}`} params={{ jobId }}>
          <phase.icon className="size-3.5" />
          <span>{phase.label}</span>
          {/* Flags the Timesheets feature as still in beta. */}
          {phase.key === "timesheets" && (
            <span className="rounded bg-violet-100 px-1 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
              Beta
            </span>
          )}
          {mailBadgeCount > 0 && (
            <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary-foreground">
              {mailBadgeCount >= 100 ? "99+" : mailBadgeCount}
            </span>
          )}
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

/**
 * Sub-items shown under Recoveries in the sidebar. Each entry links to
 * /dashboard/jobs/$jobId/recoveries/<slug>.
 */
function RecoveriesSubTabs({ jobId }: { jobId: string }) {
  const params = useParams({ strict: false }) as { section?: string };
  const activeSection = params.section;

  return (
    <SidebarMenuSub>
      {RECOVERY_SECTIONS.map((item) => (
        <SidebarMenuSubItem key={item.slug}>
          <SidebarMenuSubButton
            asChild
            size="sm"
            isActive={activeSection === item.slug}
          >
            <Link
              to="/dashboard/jobs/$jobId/recoveries/$section"
              params={{ jobId, section: item.slug }}
            >
              <item.icon className="size-3" />
              <span>{item.label}</span>
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ))}
    </SidebarMenuSub>
  );
}

/**
 * Recoveries as a collapsible group with its own chevron, matching the
 * Investigations group. State is local; it auto-opens when a recoveries
 * route is active.
 */
function RecoveriesGroup({
  jobId,
  agentRows,
}: {
  jobId: string;
  agentRows: JobAgentRow[];
}) {
  const matchRoute = useMatchRoute();
  const markSeen = useMarkAgentSeen(jobId);
  const rows = agentRows.filter((r) => r.phase === "recoveries");
  const active = !!matchRoute({
    to: "/dashboard/jobs/$jobId/recoveries",
    params: { jobId },
    fuzzy: true,
  });
  const [open, setOpen] = useState(active);
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <NavGroup
      label="Recoveries"
      icon={Scale}
      active={active}
      open={open}
      onOpenChange={setOpen}
      to="/dashboard/jobs/$jobId/recoveries"
      jobId={jobId}
      badge={<AgentGroupPill rows={rows} />}
      onHeaderClick={() => rows.length > 0 && markSeen({ phase: "recoveries" })}
    >
      <RecoveriesSubTabs jobId={jobId} />
    </NavGroup>
  );
}

/**
 * The job's reports as sub-items under the Reporting group (MOR-917). Each
 * links to the reporting phase with `?report=<id>` selecting that report.
 * Rendered only while the group is expanded, so the query doesn't run for
 * collapsed sidebars.
 */
function ReportingSubTabs({
  jobId,
  agentRows,
}: {
  jobId: string;
  agentRows: JobAgentRow[];
}) {
  const matchRoute = useMatchRoute();
  const markSeen = useMarkAgentSeen(jobId);
  const search = useSearch({ strict: false }) as { report?: string };
  const onReporting = !!matchRoute({
    to: "/dashboard/jobs/$jobId/reporting",
    params: { jobId },
    fuzzy: true,
  });
  const { data: reports } = useQuery(
    convexQuery(api.jobs.reporting.queries.listReports, {
      jobId: jobId as Id<"jobs">,
    }),
  );
  const rows = (reports ?? []) as Array<{ _id: string; title: string }>;
  if (rows.length === 0) return null;

  return (
    <SidebarMenuSub>
      {rows.slice(0, 15).map((r) => {
        const reportRows = rowsForAgent(agentRows, `report:${r._id}`);
        return (
          <SidebarMenuSubItem key={r._id}>
            <SidebarMenuSubButton
              asChild
              size="sm"
              isActive={onReporting && search.report === r._id}
            >
              <Link
                to="/dashboard/jobs/$jobId/reporting"
                params={{ jobId }}
                search={{ report: r._id }}
                onClick={() =>
                  reportRows.length > 0 &&
                  markSeen({ agentKeys: [`report:${r._id}`] })
                }
              >
                <ScrollText className="size-3" />
                <span className="truncate">{r.title}</span>
                <AgentStatusPills rows={reportRows} />
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
    </SidebarMenuSub>
  );
}

/**
 * Reporting as a collapsible group listing the job's reports (MOR-917),
 * matching the Investigations group. State is local; it auto-opens when a
 * reporting route is active.
 */
function ReportingGroup({
  jobId,
  agentRows,
}: {
  jobId: string;
  agentRows: JobAgentRow[];
}) {
  const matchRoute = useMatchRoute();
  const rows = agentRows.filter((r) => r.phase === "reporting");
  const active = !!matchRoute({
    to: "/dashboard/jobs/$jobId/reporting",
    params: { jobId },
    fuzzy: true,
  });
  const [open, setOpen] = useState(active);
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <NavGroup
      label="Reporting"
      icon={FileText}
      active={active}
      open={open}
      onOpenChange={setOpen}
      to="/dashboard/jobs/$jobId/reporting"
      jobId={jobId}
      badge={<AgentGroupPill rows={rows} />}
    >
      <ReportingSubTabs jobId={jobId} agentRows={agentRows} />
    </NavGroup>
  );
}

/**
 * Searches as a collapsible group inside Gather Information — its own chevron
 * (Registry / Web / KYC sub-tabs) for consistency with the top-level groups.
 * State is local (independent of the Gather/Data/Investigations accordion);
 * it auto-opens when a searches route is active.
 */
function SearchesGroup({ jobId }: { jobId: string }) {
  const matchRoute = useMatchRoute();
  const active = !!matchRoute({
    to: "/dashboard/jobs/$jobId/searches",
    params: { jobId },
    fuzzy: true,
  });
  const [open, setOpen] = useState(active);
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <NavGroup
      label="Searches"
      icon={Search}
      active={active}
      open={open}
      onOpenChange={setOpen}
      to="/dashboard/jobs/$jobId/searches"
      jobId={jobId}
    >
      <SearchesSubTabs jobId={jobId} />
    </NavGroup>
  );
}

/**
 * Calendar as a collapsible group with its own chevron (Key Dates / Tasks /
 * Review Windows sub-tabs), mirroring the Gather Information / Searches
 * pattern. The header navigates to the Key Dates surface (the calendar
 * landing / overview); state is local and auto-opens on any calendar route.
 */
function CalendarGroup({ jobId }: { jobId: string }) {
  const matchRoute = useMatchRoute();
  const active = !!matchRoute({
    to: "/dashboard/jobs/$jobId/calendar",
    params: { jobId },
    fuzzy: true,
  });
  const [open, setOpen] = useState(active);
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <NavGroup
      label="Tasks & Calendar"
      icon={CalendarDays}
      active={active}
      open={open}
      onOpenChange={setOpen}
      to="/dashboard/jobs/$jobId/calendar/overview"
      jobId={jobId}
    >
      <CalendarSubTabs jobId={jobId} />
    </NavGroup>
  );
}

/**
 * A collapsible nav group with a chevron toggle. Used for Gather Information,
 * Data, and Investigations. The three share a single open-state in NavMain so
 * they behave as an accordion (opening one closes the others), keeping the
 * sidebar short.
 *
 * When `to` is set the header row navigates to that overview route and the
 * chevron is the only toggle; otherwise the whole header row toggles.
 */
function NavGroup({
  label,
  icon: Icon,
  active,
  open,
  onOpenChange,
  to,
  jobId,
  badge,
  onHeaderClick,
  children,
}: {
  label: string;
  icon: ComponentType<{ className?: string }>;
  active: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional count pill shown before the chevron (agent activity). */
  badge?: ReactNode;
  onHeaderClick?: () => void;
  to?:
    | "/dashboard/jobs/$jobId/summaries"
    | "/dashboard/jobs/$jobId/review"
    | "/dashboard/jobs/$jobId/gather-information"
    | "/dashboard/jobs/$jobId/searches"
    | "/dashboard/jobs/$jobId/reporting"
    | "/dashboard/jobs/$jobId/recoveries"
    | "/dashboard/jobs/$jobId/calendar/overview";
  jobId: string;
  children: ReactNode;
}) {
  const chevron = (
    <CollapsibleTrigger asChild>
      <button
        type="button"
        aria-label={`Toggle ${label}`}
        className="absolute right-1 top-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-primary/40 outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2"
      >
        <ChevronRight className="size-3.5 transition-transform group-data-[state=open]/navgroup:rotate-90" />
      </button>
    </CollapsibleTrigger>
  );

  return (
    <Collapsible
      asChild
      open={open}
      onOpenChange={onOpenChange}
      className="group/navgroup"
    >
      <SidebarMenuSubItem className="relative">
        {to ? (
          <SidebarMenuSubButton
            asChild
            size="sm"
            isActive={active}
            className="pr-7"
          >
            <Link
              to={to}
              params={{ jobId }}
              // Open this group on header click — even when already on the
              // page (so the route-sync effect wouldn't fire). The chevron
              // still toggles it closed.
              onClick={() => {
                onOpenChange(true);
                onHeaderClick?.();
              }}
            >
              <Icon className="size-3.5" />
              <span className="truncate">{label}</span>
              {badge}
            </Link>
          </SidebarMenuSubButton>
        ) : (
          <CollapsibleTrigger asChild>
            <SidebarMenuSubButton
              size="sm"
              isActive={active}
              className="cursor-pointer pr-7"
            >
              <Icon className="size-3.5" />
              <span className="truncate">{label}</span>
              {badge}
            </SidebarMenuSubButton>
          </CollapsibleTrigger>
        )}
        {chevron}
        {/* `children` must supply its own <SidebarMenuSub> (DataSubTabs /
            ReviewSubTabs already do). `pt-1` gives the first child a bit of
            breathing room below the group header. */}
        <CollapsibleContent className="pt-1">{children}</CollapsibleContent>
      </SidebarMenuSubItem>
    </Collapsible>
  );
}

/**
 * Main navigation links in the sidebar.
 * When viewing a specific job, shows phase sub-navigation under Jobs.
 * When viewing settings, shows settings sub-navigation.
 * Conditionally shows the Admin link for super_admin users.
 */
function NavMain({ user }: { user: UserType }) {
  const matchRoute = useMatchRoute();
  const { openPalette } = useCommandPalette();

  // Get jobId from current route params (if on a job detail page)
  const params = useParams({ strict: false }) as { jobId?: string };
  const currentJobId = params.jobId;

  // Fetch the current job's name for display in sidebar
  const { data: currentJob } = useQuery({
    ...convexQueryOrIdle(
      api.jobs.queries.getJob,
      currentJobId ? { jobId: currentJobId as Id<"jobs"> } : undefined,
    ),
    enabled: !!currentJobId,
  });

  // Active organisation — the Jobs list (and its badge below) only ever
  // shows this org's jobs, so agent-activity queries must be scoped to it.
  const { orgId: navOrgId } = useActiveOrg();

  // Unseen agent updates on the current job (MOR-1013) — badges on the
  // Data / Investigations / Reporting / Recoveries groups and their rows.
  const jobAgentRows = useJobAgentActivity(currentJobId);
  // Unseen agent updates across the active org's jobs — badge on the Jobs
  // entry. Scoped to `navOrgId` so switching orgs can't leave the badge
  // showing unseen counts from a job the Jobs list no longer displays.
  const { data: agentTotals } = useQuery({
    ...convexQueryOrIdle(
      api.notifications.agentActivity.unseenByJob,
      navOrgId ? { orgId: navOrgId } : undefined,
    ),
    enabled: !!navOrgId,
  });
  const agentTotal = agentTotals?.total ?? 0;

  // Unread inbox notifications (MOR-716) — badge on the Inbox entry.
  const { data: unreadNotifications } = useQuery(
    convexQuery(api.notifications.inbox.unreadCount, {}),
  );

  // Unread mail across the user's job mailboxes (MOR-529) — badge on Mail.
  const { data: unreadMail } = useQuery(
    convexQuery(api.mail.queries.unreadCount, {}),
  );

  // Pending timesheet approvals (MOR-906) — badge on the Timesheets group
  // and its Approvals sub-item. Returns 0 for non-approvers.
  const { data: pendingApprovals } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.entries.approvalQueueCount,
      navOrgId ? { orgId: navOrgId } : undefined,
    ),
    enabled: !!navOrgId,
  });
  const { data: canApproveTimesheets } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.entries.approvalAccess,
      navOrgId ? { orgId: navOrgId } : undefined,
    ),
    enabled: !!navOrgId,
  });
  // Days of timesheets the signed-in person still owes (MOR-1461) — an
  // amber marker that stays on Timesheets until the time is entered. Keyed
  // on the user's local day (not UTC), which rolls over at local midnight.
  const localToday = useLocalToday();
  const { data: missingTimesheets } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.missingTimesheets.myMissingDays,
      navOrgId ? { orgId: navOrgId, today: localToday } : undefined,
    ),
    enabled: !!navOrgId,
  });
  const missingDays = missingTimesheets?.count ?? 0;

  const isInboxActive = !!matchRoute({ to: "/dashboard/inbox", fuzzy: false });
  const isMailActive = !!matchRoute({ to: "/dashboard/mail", fuzzy: false });
  const isWorkspaceChatActive = matchRoute({
    to: "/dashboard/agent",
    fuzzy: false,
  });
  const isJobsActive = matchRoute({ to: "/dashboard/jobs", fuzzy: true });
  const isPrecedentsActive = PRECEDENT_ITEMS.some((item) =>
    matchRoute({ to: `/dashboard/settings/${item.key}`, fuzzy: false }),
  );
  const isSettingsActive =
    matchRoute({ to: "/dashboard/settings", fuzzy: true }) &&
    !isPrecedentsActive;
  const isFirmContactsActive = matchRoute({
    to: "/dashboard/firm-contacts",
    fuzzy: true,
  });

  // Workspace Calendar groups Key Dates (/dashboard/calendar) and My Tasks
  // (/dashboard/my-tasks) under one parent, mirroring the per-job calendar's
  // Key Dates / Tasks sub-tabs rather than splitting them apart.
  const isKeyDatesActive = !!matchRoute({
    to: "/dashboard/calendar",
    fuzzy: false,
  });
  const isMyTasksActive = !!matchRoute({
    to: "/dashboard/my-tasks",
    fuzzy: false,
  });
  const isTaskStatusesActive = !!matchRoute({
    to: "/dashboard/task-statuses",
    fuzzy: false,
  });
  const isTaskLabelsActive = !!matchRoute({
    to: "/dashboard/task-labels",
    fuzzy: false,
  });
  const isWorkflowsActive = !!matchRoute({
    to: "/dashboard/workflows",
    fuzzy: true,
  });
  const isWorkflowRolesActive = !!matchRoute({
    to: "/dashboard/workflow-roles",
    fuzzy: false,
  });
  const isAutomationsActive = !!matchRoute({
    to: "/dashboard/automations",
    fuzzy: false,
  });
  const isWorkspaceCalendarActive =
    isKeyDatesActive ||
    isMyTasksActive ||
    isAutomationsActive ||
    isTaskStatusesActive ||
    isTaskLabelsActive ||
    isWorkflowsActive ||
    isWorkflowRolesActive;

  // Workspace Pipeline groups the pre-appointment pipeline board (/dashboard/
  // pre-appointment) and its config pages (statuses + labels + custom fields
  // + settings)
  // under one parent, mirroring the Tasks & Calendar group above.
  const isPipelineActive = !!matchRoute({
    to: "/dashboard/pre-appointment",
    fuzzy: false,
  });
  const isPreAppointmentStatusesActive = !!matchRoute({
    to: "/dashboard/pre-appointment-statuses",
    fuzzy: false,
  });
  const isPreAppointmentLabelsActive = !!matchRoute({
    to: "/dashboard/pre-appointment-labels",
    fuzzy: false,
  });
  const isPreAppointmentFieldsActive = !!matchRoute({
    to: "/dashboard/pre-appointment-fields",
    fuzzy: false,
  });
  const isPreAppointmentSettingsActive = !!matchRoute({
    to: "/dashboard/pre-appointment-settings",
    fuzzy: false,
  });
  const isPreAppointmentActive =
    isPipelineActive ||
    isPreAppointmentStatusesActive ||
    isPreAppointmentLabelsActive ||
    isPreAppointmentFieldsActive ||
    isPreAppointmentSettingsActive;

  // Timesheets group (MOR-906).
  const isMyTimesheetActive = !!matchRoute({
    to: "/dashboard/timesheets",
    fuzzy: false,
  });
  const isTimesheetApprovalsActive = !!matchRoute({
    to: "/dashboard/timesheet-approvals",
    fuzzy: false,
  });
  const isTimesheetSettingsActive = !!matchRoute({
    to: "/dashboard/timesheet-settings",
    fuzzy: false,
  });
  const isTimesheetReportsActive = !!matchRoute({
    to: "/dashboard/timesheet-reports",
    fuzzy: false,
  });
  const isTimesheetsActive =
    isMyTimesheetActive ||
    isTimesheetApprovalsActive ||
    isTimesheetSettingsActive ||
    isTimesheetReportsActive;

  // Per-job nav groups (Gather Information / Data / Investigations) share one
  // open-state so they behave as an accordion — opening one closes the others,
  // which keeps the sidebar short. The group containing the active page is
  // opened automatically; the chevrons let the user toggle freely otherwise.
  const dataActive =
    !!currentJobId &&
    !!matchRoute({
      to: "/dashboard/jobs/$jobId/summaries",
      params: { jobId: currentJobId },
      fuzzy: true,
    });
  const reviewActive =
    !!currentJobId &&
    !!matchRoute({
      to: "/dashboard/jobs/$jobId/review",
      params: { jobId: currentJobId },
      fuzzy: true,
    });
  const gatherActive =
    !!currentJobId &&
    (!!matchRoute({
      to: "/dashboard/jobs/$jobId/gather-information",
      params: { jobId: currentJobId },
      fuzzy: false,
    }) ||
      !!matchRoute({
        to: "/dashboard/jobs/$jobId/searches",
        params: { jobId: currentJobId },
        fuzzy: true,
      }) ||
      !!matchRoute({
        to: "/dashboard/jobs/$jobId/day-one",
        params: { jobId: currentJobId },
        fuzzy: false,
      }) ||
      !!matchRoute({
        to: "/dashboard/jobs/$jobId/gather",
        params: { jobId: currentJobId },
        fuzzy: true,
      }));
  // Pre-appointment jobs hide the post-appointment phases (Data,
  // Investigations, Reporting) until the job is converted.
  const jobPhaseItems =
    currentJob?.status === "pre_appointment"
      ? JOB_PHASE_ITEMS.filter(
          (phase) => !POST_APPOINTMENT_ONLY_PHASES.has(phase.key),
        )
      : JOB_PHASE_ITEMS;

  const activeGroup: NavGroupKey | null = dataActive
    ? "data"
    : reviewActive
      ? "investigations"
      : gatherActive
        ? "gather"
        : null;
  const [openGroup, setOpenGroup] = useState<NavGroupKey | null>(activeGroup);
  useEffect(() => {
    if (activeGroup) setOpenGroup(activeGroup);
  }, [activeGroup]);

  // Platform-aware shortcut hint: "⌘K" on Mac, "Ctrl+K" on Windows/Linux.
  const mod = modKeyLabel();
  const searchShortcut = mod === "⌘" ? "⌘K" : `${mod}+K`;

  return (
    <SidebarGroup className="p-2">
      <SidebarGroupContent>
        <SidebarMenu>
          {/* Notifications — the user's cross-job notification feed (MOR-716).
              Deliberately first: it's the "what needs me" entry point. */}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Notifications"
              isActive={isInboxActive}
            >
              <Link
                to="/dashboard/inbox"
                className={cn(
                  isInboxActive
                    ? "font-medium text-primary"
                    : "text-primary/60",
                )}
              >
                <Bell className="size-4" />
                <span className="text-sm">Notifications</span>
                {(unreadNotifications ?? 0) > 0 && (
                  <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary-foreground">
                    {(unreadNotifications ?? 0) >= 100
                      ? "99+"
                      : unreadNotifications}
                  </span>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Inbox — the user's cross-job email surface (MOR-529). Sits
              between Notifications and Search; per-job mailboxes are enabled
              from each job's own Inbox page. */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Inbox" isActive={isMailActive}>
              <Link
                to="/dashboard/mail"
                className={cn(
                  isMailActive ? "font-medium text-primary" : "text-primary/60",
                )}
              >
                <Mail className="size-4" />
                <span className="text-sm">Inbox</span>
                {(unreadMail ?? 0) > 0 && (
                  <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary-foreground">
                    {(unreadMail ?? 0) >= 100 ? "99+" : unreadMail}
                  </span>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Workspace Search — opens the global
              Cmd+K command palette where the user can search across
              every job they have access to and/or ask Morgan. We use
              a button (not a Link) because the palette is a modal,
              not a route — and Cmd+K / Ctrl+K still works globally
              (see CommandPalette mounted in `_layout.tsx`). */}
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => openPalette()}
              tooltip={`Search (${searchShortcut})`}
              className="cursor-pointer text-primary/60"
            >
              <Search className="size-4" />
              <span className="text-sm">Search</span>
              <kbd className="ml-auto rounded border border-border bg-card/50 px-1.5 py-0.5 text-[10px] font-medium text-primary/50 group-data-[collapsible=icon]:hidden">
                {searchShortcut}
              </kbd>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Workspace Chat */}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={!!isWorkspaceChatActive}
              tooltip="Workspace Chat"
            >
              <Link
                to="/dashboard/agent"
                className={cn(
                  isWorkspaceChatActive
                    ? "font-medium text-primary"
                    : "text-primary/60",
                )}
              >
                <Logo compact className="size-4" />
                <span className="text-sm">Workspace Chat</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Jobs — shows phase sub-nav when viewing a specific job */}
          <Collapsible
            asChild
            open={!!isJobsActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Jobs"
                isActive={!!isJobsActive}
              >
                <Link
                  to="/dashboard/jobs"
                  className={cn(
                    isJobsActive
                      ? "font-medium text-primary"
                      : "text-primary/60",
                  )}
                >
                  <Briefcase className="size-4" />
                  <span className="text-sm">Jobs</span>
                  {agentTotal > 0 && (
                    <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary-foreground">
                      {agentTotal >= 100 ? "99+" : agentTotal}
                    </span>
                  )}
                </Link>
              </SidebarMenuButton>

              {/* Phase sub-items — only shown when a job is selected */}
              {currentJobId && (
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {/* Current job name as a label */}
                    <li className="px-2 pb-1 pt-0.5">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-primary/40">
                        {currentJob?.entityName ?? "Loading..."}
                      </span>
                    </li>
                    {jobPhaseItems.map((phase) => {
                      // Data and Investigations render as collapsible accordion
                      // groups rather than plain rows.
                      if (phase.key === "summaries") {
                        return (
                          <NavGroup
                            key={phase.key}
                            label="Data"
                            icon={Database}
                            active={dataActive}
                            open={openGroup === "data"}
                            onOpenChange={(o) =>
                              setOpenGroup(o ? "data" : null)
                            }
                            to="/dashboard/jobs/$jobId/summaries"
                            jobId={currentJobId}
                            badge={
                              <AgentGroupPill
                                rows={jobAgentRows.filter(
                                  (r) => r.phase === "data",
                                )}
                              />
                            }
                          >
                            <DataSubTabs
                              jobId={currentJobId}
                              jobStatus={currentJob?.status}
                              appointmentType={currentJob?.appointmentType}
                              agentRows={jobAgentRows}
                            />
                          </NavGroup>
                        );
                      }
                      if (phase.key === "review") {
                        return (
                          <Fragment key={phase.key}>
                            <NavGroup
                              label="Investigations"
                              icon={ScanSearch}
                              active={reviewActive}
                              open={openGroup === "investigations"}
                              onOpenChange={(o) =>
                                setOpenGroup(o ? "investigations" : null)
                              }
                              to="/dashboard/jobs/$jobId/review"
                              jobId={currentJobId}
                              badge={
                                <AgentGroupPill
                                  rows={jobAgentRows.filter(
                                    (r) => r.phase === "investigations",
                                  )}
                                />
                              }
                            >
                              <ReviewSubTabs
                                jobId={currentJobId}
                                appointmentType={currentJob?.appointmentType}
                                agentRows={jobAgentRows}
                              />
                            </NavGroup>
                            <ProofOfDebtRow
                              jobId={currentJobId}
                              agentRows={jobAgentRows}
                            />
                          </Fragment>
                        );
                      }
                      // Reporting renders as its own collapsible group listing
                      // the job's reports (MOR-917).
                      if (phase.key === "reporting") {
                        return (
                          <ReportingGroup
                            key={phase.key}
                            jobId={currentJobId}
                            agentRows={jobAgentRows}
                          />
                        );
                      }
                      // Recoveries renders as its own collapsible group
                      // (chevron + section sub-tabs), matching Investigations.
                      if (phase.key === "recoveries") {
                        return (
                          <RecoveriesGroup
                            key={phase.key}
                            jobId={currentJobId}
                            agentRows={jobAgentRows}
                          />
                        );
                      }
                      // Calendar renders as its own collapsible group (chevron
                      // + Overview / Key Dates / Tasks / Review Windows), with
                      // independent open-state so it isn't tied to the
                      // Gather/Data/Investigations accordion.
                      if (phase.key === "calendar") {
                        return (
                          <CalendarGroup key={phase.key} jobId={currentJobId} />
                        );
                      }
                      return (
                        <Fragment key={phase.key}>
                          <JobPhaseRow jobId={currentJobId} phase={phase} />
                          {/* Gather Information sits under Mail (which sits
                              under Job Chat), before Data. */}
                          {phase.key === "mail" && (
                            <NavGroup
                              label="Gather Information"
                              icon={Inbox}
                              active={gatherActive}
                              open={openGroup === "gather"}
                              onOpenChange={(o) =>
                                setOpenGroup(o ? "gather" : null)
                              }
                              to="/dashboard/jobs/$jobId/gather-information"
                              jobId={currentJobId}
                            >
                              {/* `mr-0 pr-0` keeps these rows' right edge flush
                                  with the top-level groups so the nested
                                  Searches chevron lines up with the others. */}
                              <SidebarMenuSub className="mr-0 pr-0">
                                {GATHER_ITEMS.map((item) =>
                                  item.key === "searches" ? (
                                    <SearchesGroup
                                      key={item.key}
                                      jobId={currentJobId}
                                    />
                                  ) : (
                                    <JobPhaseRow
                                      key={item.key}
                                      jobId={currentJobId}
                                      phase={item}
                                    />
                                  ),
                                )}
                              </SidebarMenuSub>
                            </NavGroup>
                          )}
                        </Fragment>
                      );
                    })}
                  </SidebarMenuSub>
                </CollapsibleContent>
              )}
            </SidebarMenuItem>
          </Collapsible>

          {/* Workspace Pipeline — the pre-appointment pipeline board
              (prospective jobs before conversion to an appointment) plus its
              firm-level config pages (pipeline statuses + labels), grouped
              under one parent mirroring the Tasks & Calendar group below. */}
          <Collapsible
            asChild
            open={isPreAppointmentActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Pipeline"
                isActive={isPreAppointmentActive}
              >
                <Link
                  to="/dashboard/pre-appointment"
                  className={cn(
                    isPreAppointmentActive
                      ? "font-medium text-primary"
                      : "text-primary/60",
                  )}
                >
                  <Workflow className="size-4" />
                  <span className="text-sm">Pipeline</span>
                </Link>
              </SidebarMenuButton>

              <CollapsibleContent>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isPipelineActive}
                    >
                      <Link to="/dashboard/pre-appointment">
                        <SquareKanban className="size-3.5" />
                        <span>Pipeline Tracker</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isPreAppointmentStatusesActive}
                    >
                      <Link to="/dashboard/pre-appointment-statuses">
                        <CircleDot className="size-3.5" />
                        <span>Stages</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isPreAppointmentLabelsActive}
                    >
                      <Link to="/dashboard/pre-appointment-labels">
                        <Tag className="size-3.5" />
                        <span>Labels</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isPreAppointmentFieldsActive}
                    >
                      <Link to="/dashboard/pre-appointment-fields">
                        <TableProperties className="size-3.5" />
                        <span>Custom Fields</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isPreAppointmentSettingsActive}
                    >
                      <Link to="/dashboard/pre-appointment-settings">
                        <Settings className="size-3.5" />
                        <span>Settings</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>

          {/* Timesheets (MOR-906) — weekly entry, partner approvals and firm
              config (categories, grades/rates, staff codes). */}
          <Collapsible
            asChild
            open={isTimesheetsActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Timesheets"
                isActive={isTimesheetsActive}
              >
                <Link
                  to="/dashboard/timesheets"
                  className={cn(
                    isTimesheetsActive
                      ? "font-medium text-primary"
                      : "text-primary/60",
                  )}
                >
                  <Timer className="size-4" />
                  <span className="text-sm">Timesheets</span>
                  {/* Flags the Timesheets feature as still in beta. */}
                  <span className="rounded bg-violet-100 px-1 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                    Beta
                  </span>
                  {!isTimesheetsActive && (pendingApprovals ?? 0) > 0 && (
                    <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary-foreground">
                      {(pendingApprovals ?? 0) >= 100
                        ? "99+"
                        : pendingApprovals}
                    </span>
                  )}
                </Link>
              </SidebarMenuButton>

              <CollapsibleContent>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isMyTimesheetActive}
                    >
                      <Link to="/dashboard/timesheets">
                        <Timer className="size-3.5" />
                        <span>My Timesheets</span>
                        {missingDays > 0 && (
                          <span
                            className="ml-auto rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white"
                            aria-label={
                              missingDays === 1
                                ? "1 day of timesheets missing"
                                : `${missingDays} days of timesheets missing`
                            }
                            title={
                              missingDays === 1
                                ? "1 day of timesheets missing"
                                : `${missingDays} days of timesheets missing`
                            }
                          >
                            {missingDays >= 100 ? "99+" : missingDays}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  {canApproveTimesheets && (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        size="sm"
                        isActive={isTimesheetApprovalsActive}
                      >
                        <Link to="/dashboard/timesheet-approvals">
                          <BadgeCheck className="size-3.5" />
                          <span>Approvals</span>
                          {(pendingApprovals ?? 0) > 0 && (
                            <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary-foreground">
                              {(pendingApprovals ?? 0) >= 100
                                ? "99+"
                                : pendingApprovals}
                            </span>
                          )}
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )}
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isTimesheetReportsActive}
                    >
                      <Link to="/dashboard/timesheet-reports">
                        <ChartColumn className="size-3.5" />
                        <span>Analytics and Reports</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isTimesheetSettingsActive}
                    >
                      <Link to="/dashboard/timesheet-settings">
                        <Settings className="size-3.5" />
                        <span>Settings</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>

          {/* Workspace Calendar — aggregates key dates and tasks across every
              job the user has access to in the active org. Key Dates and My
              Tasks live together as sub-tabs here (mirroring the per-job
              calendar's Key Dates / Tasks split) rather than as separate
              top-level entries. Job-level calendar lives in the Jobs sub-nav. */}
          <Collapsible
            asChild
            open={isWorkspaceCalendarActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Calendar"
                isActive={isWorkspaceCalendarActive}
              >
                <Link
                  to="/dashboard/my-tasks"
                  className={cn(
                    isWorkspaceCalendarActive
                      ? "font-medium text-primary"
                      : "text-primary/60",
                  )}
                >
                  <CalendarDays className="size-4" />
                  <span className="text-sm">Tasks &amp; Calendar</span>
                </Link>
              </SidebarMenuButton>

              <CollapsibleContent>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isMyTasksActive}
                    >
                      <Link to="/dashboard/my-tasks">
                        <ListChecks className="size-3.5" />
                        <span>My Tasks</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isKeyDatesActive}
                    >
                      <Link to="/dashboard/calendar">
                        <CalendarDays className="size-3.5" />
                        <span>Calendar</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isTaskStatusesActive}
                    >
                      <Link to="/dashboard/task-statuses">
                        <CircleDot className="size-3.5" />
                        <span>Statuses</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isTaskLabelsActive}
                    >
                      <Link to="/dashboard/task-labels">
                        <Tag className="size-3.5" />
                        <span>Labels</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isWorkflowsActive}
                    >
                      <Link to="/dashboard/workflows">
                        <LayoutTemplate className="size-3.5" />
                        <span>Workflows</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isWorkflowRolesActive}
                    >
                      <Link to="/dashboard/workflow-roles">
                        <UserCog className="size-3.5" />
                        <span>Roles</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      size="sm"
                      isActive={isAutomationsActive}
                    >
                      <Link to="/dashboard/automations">
                        <Zap className="size-3.5" />
                        <span>Automations</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>

          {/* Firm Contacts — visible to all members; editing is gated
              to owners/admins within each page. Shows a sub-nav (Directory
              + Fields) when on a firm-contacts page. */}
          <Collapsible
            asChild
            open={!!isFirmContactsActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Firm Contacts"
                isActive={!!isFirmContactsActive}
              >
                <Link
                  to="/dashboard/firm-contacts"
                  className={cn(
                    isFirmContactsActive
                      ? "font-medium text-primary"
                      : "text-primary/60",
                  )}
                >
                  <Contact className="size-4" />
                  <span className="text-sm">Firm Contacts</span>
                </Link>
              </SidebarMenuButton>

              <CollapsibleContent>
                <SidebarMenuSub>
                  {FIRM_CONTACT_ITEMS.map((item) => {
                    const itemPath = item.key
                      ? `/dashboard/firm-contacts/${item.key}`
                      : "/dashboard/firm-contacts";
                    const itemActive = matchRoute({
                      to: itemPath,
                      fuzzy: false,
                    });
                    return (
                      <SidebarMenuSubItem key={item.key || "directory"}>
                        <SidebarMenuSubButton
                          asChild
                          size="sm"
                          isActive={!!itemActive}
                        >
                          <Link to={itemPath}>
                            <item.icon className="size-3.5" />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    );
                  })}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>

          {/* Precedents — shows sub-nav when on precedent pages */}
          <Collapsible
            asChild
            open={!!isPrecedentsActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Precedents"
                isActive={!!isPrecedentsActive}
              >
                <Link
                  to="/dashboard/settings/docs"
                  className={cn(
                    isPrecedentsActive
                      ? "font-medium text-primary"
                      : "text-primary/60",
                  )}
                >
                  <FileStack className="size-4" />
                  <span className="text-sm">Precedents</span>
                </Link>
              </SidebarMenuButton>

              <CollapsibleContent>
                <SidebarMenuSub>
                  {PRECEDENT_ITEMS.map((item) => {
                    const itemActive = matchRoute({
                      to: `/dashboard/settings/${item.key}`,
                      fuzzy: false,
                    });

                    return (
                      <SidebarMenuSubItem key={item.key}>
                        <SidebarMenuSubButton
                          asChild
                          size="sm"
                          isActive={!!itemActive}
                        >
                          <Link to={`/dashboard/settings/${item.key}`}>
                            <item.icon className="size-3.5" />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    );
                  })}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>

          {/* Settings — shows sub-nav when on settings pages */}
          <Collapsible
            asChild
            open={!!isSettingsActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Settings"
                isActive={!!isSettingsActive}
              >
                <Link
                  to="/dashboard/settings"
                  className={cn(
                    isSettingsActive
                      ? "font-medium text-primary"
                      : "text-primary/60",
                  )}
                >
                  <Settings className="size-4" />
                  <span className="text-sm">Settings</span>
                </Link>
              </SidebarMenuButton>

              <CollapsibleContent>
                <SidebarMenuSub>
                  {SETTINGS_ITEMS.map((item) => {
                    const settingPath = item.key
                      ? `/dashboard/settings/${item.key}`
                      : "/dashboard/settings";
                    const itemActive = matchRoute({
                      to: settingPath,
                      fuzzy: false,
                    });

                    return (
                      <SidebarMenuSubItem key={item.key || "general"}>
                        <SidebarMenuSubButton
                          asChild
                          size="sm"
                          isActive={!!itemActive}
                        >
                          <Link to={settingPath}>
                            <item.icon className="size-3.5" />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    );
                  })}

                  {/* Support section */}
                  <li className="px-2 pb-1 pt-2">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-primary/40">
                      Support
                    </span>
                  </li>
                  {(() => {
                    const helpPath = `/dashboard/settings/help`;
                    const helpActive = matchRoute({
                      to: helpPath,
                      fuzzy: false,
                    });
                    return (
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          size="sm"
                          isActive={!!helpActive}
                        >
                          <Link to={helpPath}>
                            <LifeBuoy className="size-3.5" />
                            <span>Help</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    );
                  })()}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>

          {/* Admin (super_admin only) */}
          {user.systemRole === "super_admin" && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={!!matchRoute({ to: "/admin", fuzzy: true })}
                tooltip="Admin"
              >
                <Link
                  to="/admin"
                  className={cn(
                    matchRoute({ to: "/admin", fuzzy: true })
                      ? "font-medium text-primary"
                      : "text-primary/60",
                  )}
                >
                  <Shield className="size-4" />
                  <span className="text-sm">Admin</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

// ---------------------------------------------------------------------------
// User Menu
// ---------------------------------------------------------------------------

/**
 * User menu in the sidebar footer.
 * Expands inline (collapsible) instead of a popover.
 */
function UserMenu({ user }: { user: UserType }) {
  const signOut = useSignOut();

  return (
    <SidebarMenu>
      <Collapsible asChild className="group/user">
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton size="lg" className="cursor-pointer">
              <UserAvatar
                avatarUrl={user.avatarUrl}
                firstName={user.firstName}
                lastName={user.lastName}
                email={user.email}
                className="h-8 w-8"
              />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium text-primary/80">
                  {getDisplayName(user.firstName, user.lastName, user.email)}
                </span>
                <span className="truncate text-xs text-primary/50">
                  {user.email}
                </span>
              </div>
              <ChevronUp className="ml-auto size-4 text-primary/40 transition-transform group-data-[state=open]/user:rotate-180" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              <SidebarMenuSubItem>
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-xs text-primary/60">Theme</span>
                  <ThemeSwitcherSidebar />
                </div>
              </SidebarMenuSubItem>
              <SidebarMenuSubItem>
                <SidebarMenuSubButton
                  asChild
                  size="sm"
                  className="cursor-pointer"
                >
                  <button type="button" onClick={() => signOut()}>
                    <LogOut className="size-3.5" />
                    <span>Sign out</span>
                  </button>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    </SidebarMenu>
  );
}
